// El panel de arriba de la tabla: a qué tienda va el lanzamiento, si está conectada
// y en qué paso está — ADR-2026-10-05-la-tabla-de-la-tienda-dice-que-falta.
//
// Antes el detalle abría directo en la tabla con un renglón gris ("Tienda Nube · 3
// productos · 4 fotos") y un chip chico de "Tiendas". Quien entraba por primera vez
// no sabía qué tenía que hacer ni dónde iba a aparecer lo que publicara. El panel
// dice las dos cosas: la tienda con su logo y su conexión, y los tres pasos del
// lanzamiento con cuánto falta de cada uno.
import { Check, Loader2, Plus } from 'lucide-react';
import IconoDeCanal from './IconoDeCanal';
import { nombreCanal, nombresDeDestinos, type CanalLanzamiento } from './canales';
import type { TonoDeEstado } from './estadoDeFila';

/** Lo que se sabe de la conexión de cada tienda. `null` = todavía no se sabe. */
export type ConexionDeTienda = { conectada: boolean; nombre: string | null } | null;

export function PanelDeLaTienda({
  destinos,
  opciones,
  exportaCarpetas,
  sinPublicar,
  conexiones,
  cuenta,
  total,
  fotos,
  bloqueado,
  onCambiarDestinos,
  onConectarTiendanube,
}: {
  destinos: readonly CanalLanzamiento[];
  /** Las tiendas que se pueden sumar o sacar. Vacío = el destino es fijo (ZIP, VTEX). */
  opciones: readonly CanalLanzamiento[];
  exportaCarpetas: boolean;
  /** Destinos que se arman pero todavía no se pueden publicar (VTEX). */
  sinPublicar: readonly CanalLanzamiento[];
  conexiones: Partial<Record<CanalLanzamiento, ConexionDeTienda>>;
  cuenta: Record<TonoDeEstado, number>;
  total: number;
  fotos: number;
  /** Publicando o guardando destinos: las tiendas no se tocan. */
  bloqueado: boolean;
  onCambiarDestinos: (destinos: CanalLanzamiento[]) => void;
  /** Abre el modal de conectar Tienda Nube: el mismo que aparece al publicar sin conexión. */
  onConectarTiendanube: () => void;
}) {
  const listos = cuenta.listo + cuenta.publicado + cuenta.publicando + cuenta.error;
  const publicados = cuenta.publicado;
  const pasos = pasosDelLanzamiento({ total, fotos, faltan: cuenta.falta, publicados, cuenta, exportaCarpetas, destinos });
  const avance = total === 0 ? 0 : Math.round(((listos + publicados) / (total * 2)) * 100);

  return (
    <section
      aria-label="Tienda y progreso del lanzamiento"
      data-tour="tienda-panel"
      className="mt-5 overflow-hidden rounded-2xl border border-[#1644EB]/10 bg-gradient-to-br from-[#F3F6FF] via-white to-white shadow-sm"
    >
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex shrink-0 -space-x-2">
            {destinos.map((d) => (
              <span key={d} className="rounded-[10px] ring-2 ring-white">
                <IconoDeCanal canal={d} />
              </span>
            ))}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-brand-black">
              {exportaCarpetas ? 'Se descarga como ZIP' : `Se publica en ${nombresDeDestinos(destinos)}`}
            </p>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
              {exportaCarpetas ? (
                <span className="text-xs text-neutral-500">
                  Las fotos salen en carpetas por categoría, listas para subir a cualquier plataforma.
                </span>
              ) : (
                destinos.map((d) => (
                  <EstadoDeConexion
                    key={d}
                    nombre={nombreCanal(d)}
                    varias={destinos.length > 1}
                    conexion={conexiones[d]}
                    proximamente={sinPublicar.includes(d)}
                    onConectar={d === 'tiendanube' ? onConectarTiendanube : undefined}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        {opciones.length > 1 && (
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Tiendas del lanzamiento">
            {opciones.map((d) => {
              const puesta = destinos.includes(d);
              const ultima = puesta && destinos.length === 1;
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={puesta}
                  disabled={bloqueado || ultima}
                  onClick={() => onCambiarDestinos(puesta ? destinos.filter((x) => x !== d) : [...destinos, d])}
                  title={
                    ultima
                      ? 'Un lanzamiento va al menos a una tienda'
                      : puesta
                        ? `Sacar ${nombreCanal(d)} de este lanzamiento`
                        : `Publicar también en ${nombreCanal(d)}`
                  }
                  className={`inline-flex items-center gap-1.5 rounded-full border py-1.5 pl-2.5 pr-3 text-xs font-medium transition-colors disabled:cursor-default ${
                    puesta
                      ? 'border-[#1644EB] bg-[#1644EB] text-white'
                      : 'border-dashed border-[#1644EB]/40 bg-white text-[#1644EB] hover:bg-[#EEF2FF]'
                  }`}
                >
                  {puesta ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                  {nombreCanal(d)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Teléfono: un stepper de tres columnas, como el de la lista de Tienda online, y abajo
          sólo lo que pide el paso en curso. Uno abajo del otro, los tres pasos con su detalle
          ocupaban media pantalla antes de llegar a los productos
          (ADR-2026-10-16-el-detalle-del-lanzamiento-en-el-telefono). */}
      <div className="border-t border-[#1644EB]/10 bg-white/70 px-3 pt-3 pb-3 sm:hidden">
        <ol className="grid grid-cols-3">
          {pasos.map((paso, i) => (
            <li key={paso.titulo} className="relative flex flex-col items-center gap-1.5 px-1 text-center">
              {i > 0 && (
                <span
                  aria-hidden="true"
                  className={`absolute right-[calc(50%+18px)] top-3.5 h-0.5 w-[calc(100%-36px)] rounded-full ${
                    pasos[i - 1]!.estado === 'hecho' ? 'bg-emerald-400' : 'bg-neutral-200'
                  }`}
                />
              )}
              <NumeroDePaso numero={i + 1} estado={paso.estado} />
              <span
                className={`text-xs font-medium leading-tight ${paso.estado === 'pendiente' ? 'text-neutral-400' : 'text-brand-black'}`}
              >
                {TITULO_CORTO[i]!(exportaCarpetas)}
              </span>
            </li>
          ))}
        </ol>
        <EnCurso pasos={pasos} />
      </div>
      <ol className="hidden gap-4 border-t border-[#1644EB]/10 bg-white/70 p-5 sm:grid sm:grid-cols-3">
        {pasos.map((paso, i) => (
          <li key={paso.titulo} className="flex items-start gap-3">
            <NumeroDePaso numero={i + 1} estado={paso.estado} />
            <span className="min-w-0">
              <span
                className={`block text-sm font-medium ${paso.estado === 'pendiente' ? 'text-neutral-500' : 'text-brand-black'}`}
              >
                {paso.titulo}
              </span>
              <span
                className={`mt-0.5 block text-xs ${
                  paso.alerta ? 'text-amber-700' : paso.estado === 'hecho' ? 'text-emerald-700' : 'text-neutral-500'
                }`}
              >
                {paso.detalle}
              </span>
            </span>
          </li>
        ))}
      </ol>
      <div
        className="h-1 bg-[#1644EB]/10"
        role="progressbar"
        aria-label="Avance del lanzamiento"
        aria-valuenow={avance}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-r-full bg-gradient-to-r from-[#1644EB] to-[#60E7F0] transition-[width] duration-500"
          style={{ width: `${avance}%` }}
        />
      </div>
    </section>
  );
}

function EstadoDeConexion({
  nombre,
  varias,
  conexion,
  proximamente,
  onConectar,
}: {
  nombre: string;
  /** Con varias tiendas cada renglón dice de cuál habla. */
  varias: boolean;
  conexion: ConexionDeTienda | undefined;
  proximamente: boolean;
  /** Sin él, "sin conectar" queda como aviso; con él, se puede conectar desde acá. */
  onConectar?: () => void;
}) {
  const de = varias ? `${nombre}: ` : '';
  if (proximamente) {
    return <Linea punto="bg-neutral-300" texto={`${de}la conexión para publicar llega pronto`} />;
  }
  if (conexion === undefined) return null;
  if (conexion === null) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-neutral-400">
        <Loader2 className="h-3 w-3 animate-spin" /> {de}revisando la conexión…
      </span>
    );
  }
  if (conexion.conectada) {
    return (
      <Linea
        punto="bg-emerald-500"
        texto={`${de}conectada${conexion.nombre ? ` · ${conexion.nombre}` : ''}`}
        clase="text-emerald-700"
      />
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2">
      <Linea
        punto="bg-amber-400"
        texto={onConectar ? `${de}sin conectar` : `${de}sin conectar — te la pedimos al publicar`}
        clase="text-amber-700"
      />
      {onConectar && (
        <button
          type="button"
          onClick={onConectar}
          className="text-xs font-medium text-[#1644EB] underline-offset-2 hover:underline"
        >
          Conectar ahora
        </button>
      )}
    </span>
  );
}

function Linea({ punto, texto, clase = 'text-neutral-500' }: { punto: string; texto: string; clase?: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs ${clase}`}>
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${punto}`} aria-hidden="true" />
      {texto}
    </span>
  );
}

type EstadoDePaso = 'hecho' | 'actual' | 'pendiente';

/** El nombre de cada paso en el stepper del teléfono, donde cada uno tiene un tercio del ancho. */
const TITULO_CORTO: ReadonlyArray<(zip: boolean) => string> = [
  () => 'Sumá productos',
  () => 'Completá fichas',
  (zip) => (zip ? 'Descargá el ZIP' : 'Publicá'),
];

/**
 * Lo que pide el paso en curso, en un renglón (teléfono). Con todo hecho, lo que dice el
 * último ("Ya están todos publicados").
 */
function EnCurso({ pasos }: { pasos: readonly Paso[] }) {
  const i = Math.max(
    0,
    pasos.findIndex((p) => p.estado === 'actual'),
  );
  const enCurso = pasos.some((p) => p.estado === 'actual') ? pasos[i]! : pasos[pasos.length - 1]!;
  const hecho = enCurso.estado === 'hecho';
  return (
    <p
      className={`mt-3 flex items-center gap-2 rounded-xl py-2 px-3 text-xs ${
        enCurso.alerta
          ? 'bg-amber-50 text-amber-800'
          : hecho
            ? 'bg-emerald-50 text-emerald-800'
            : 'bg-[#EEF2FF] text-[#1644EB]'
      }`}
    >
      <span
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${enCurso.alerta ? 'bg-amber-500' : hecho ? 'bg-emerald-500' : 'bg-[#1644EB]'}`}
        aria-hidden="true"
      />
      <span className="min-w-0">
        {!hecho && <span className="font-medium">Paso {pasos.indexOf(enCurso) + 1}: </span>}
        {enCurso.detalle}
      </span>
    </p>
  );
}

function NumeroDePaso({ numero, estado }: { numero: number; estado: EstadoDePaso }) {
  if (estado === 'hecho') {
    return (
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-emerald-500 text-white" aria-label="Hecho">
        <Check className="h-4 w-4" strokeWidth={2.5} />
      </span>
    );
  }
  return (
    <span
      className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-semibold ${
        estado === 'actual' ? 'bg-[#1644EB] text-white shadow-[0_0_0_4px_rgba(22,68,235,0.12)]' : 'bg-neutral-100 text-neutral-400'
      }`}
    >
      {numero}
    </span>
  );
}

interface Paso {
  titulo: string;
  detalle: string;
  estado: EstadoDePaso;
  /** El detalle en ámbar: algo frena este paso. */
  alerta?: boolean;
}

const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;

/** Los tres pasos y en cuál está el lanzamiento. Exportada para los tests. */
export function pasosDelLanzamiento(x: {
  total: number;
  fotos: number;
  faltan: number;
  publicados: number;
  cuenta: Record<TonoDeEstado, number>;
  exportaCarpetas: boolean;
  destinos: readonly CanalLanzamiento[];
}): [Paso, Paso, Paso] {
  const hayProductos = x.total > 0;
  const fichasListas = hayProductos && x.faltan === 0;
  const todoPublicado = hayProductos && x.publicados === x.total;

  const sumar: Paso = {
    titulo: 'Sumá productos',
    detalle: hayProductos
      ? `${plural(x.total, 'producto', 'productos')} con ${plural(x.fotos, 'foto', 'fotos')}`
      : 'Elegí fotos de tus creaciones',
    estado: hayProductos ? 'hecho' : 'actual',
  };

  const completar: Paso = {
    titulo: 'Completá las fichas',
    detalle: !hayProductos
      ? 'Precio, stock y variantes'
      : fichasListas
        ? 'Todas tienen lo necesario'
        : `${x.faltan === 1 ? 'A 1 le falta' : `A ${x.faltan} les falta`} algo: tocá Modificar`,
    estado: !hayProductos ? 'pendiente' : fichasListas ? 'hecho' : 'actual',
    alerta: hayProductos && !fichasListas,
  };

  const donde = x.exportaCarpetas ? '' : ` en ${nombresDeDestinos(x.destinos)}`;
  const publicar: Paso = {
    titulo: x.exportaCarpetas ? 'Descargá el ZIP' : `Publicá${donde}`,
    detalle: todoPublicado
      ? x.total === 1
        ? 'Ya está publicado'
        : 'Ya están todos publicados'
      : x.cuenta.publicando > 0
        ? 'Publicando…'
        : x.cuenta.error > 0
          ? `${plural(x.cuenta.error, 'no se pudo publicar', 'no se pudieron publicar')}`
          : x.publicados > 0
            ? `${x.publicados} de ${x.total} publicados`
            : x.exportaCarpetas
              ? 'Baja lo tildado, en carpetas'
              : 'Sale lo que está tildado',
    // Un solo paso "actual" a la vez: con fichas a medio completar, el que pide la
    // atención es el 2, aunque lo listo ya se pueda publicar.
    estado: todoPublicado ? 'hecho' : fichasListas ? 'actual' : 'pendiente',
    alerta: x.cuenta.error > 0,
  };

  return [sumar, completar, publicar];
}
